import React, { memo, useRef, useState, useCallback, useEffect } from 'react'
import styles from './CommonFileUploadInput.module.scss'
import { UseFormRegisterReturn } from 'react-hook-form';
import { toast } from 'react-toastify';

export enum StyleOfFileInput {
    ButtonFirst = 'buttonFirst',
    FormInput = 'formInput'
}

type Props = {
    styleOfInput?: StyleOfFileInput;
    initialFiles?: string[];
    registerInputProps?: UseFormRegisterReturn<any>;
    maxFileSize: number;
    multiple?: boolean;
    accept?: string;
    onFilesSelect: (files: File[]) => void
}

const CommonFileUploadInput: React.FC<Props> = memo((props: Props) => {
    const { onFilesSelect, registerInputProps, multiple, accept, initialFiles, styleOfInput, maxFileSize } = props;

    const fileInputRef = useRef<HTMLInputElement>(null);

    const [fileNames, setFileNames] = useState<string[]>([]);

    const handleFileSelect = useCallback(() => {
        fileInputRef.current?.click();
    }, [fileInputRef]);

    const fileSizeLimit = maxFileSize * 1024 * 1024;

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const fileNames: string[] = [];
        const files: File[] = [];

        if (event.target.files) {
            for (let i = 0; i < event.target.files!.length; i++) {
                if (event.target.files![i].size > fileSizeLimit) {
                    toast.warn(`File size is too large. Maximum file size is ${maxFileSize}MB`);
                    return;
                }
                fileNames.push(event.target.files![i].name);
                files.push(event.target.files![i]);
            }
        }

        setFileNames(fileNames);
        onFilesSelect(files);
    };

    const styleOfInputClass = () => {
        switch (styleOfInput) {
            case StyleOfFileInput.ButtonFirst:
                return styles.buttonFirstLayout;
            case StyleOfFileInput.FormInput:
                return styles.formInputLayout;
            default:
                return styles.buttonFirstLayout;
        }
    }

    useEffect(() => {
        setFileNames(initialFiles ?? []);
    }, [initialFiles]);

    return (
        <div className={`${styles.container} ${styleOfInputClass()}`}>
            <input
                type="file"
                accept={accept}
                multiple={multiple}
                {...registerInputProps}
                ref={fileInputRef}
                onChange={handleFileChange}
                hidden />
            <button
                className={`standardButton ${styles.uploadButton}`}
                onClick={handleFileSelect}>
                Select File
            </button>
            <div className={styles.fileNameContainer}>
                {
                    fileNames.map((fileName, index) => (
                        <div className={styles.fileNameElement} key={fileName + index}>
                            <svg width="15" height="11" viewBox="0 0 15 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5.45802 7.95593L12.4309 0.983117C12.6208 0.793201 12.8461 0.698242 13.1068 0.698242C13.3675 0.698242 13.5928 0.793201 13.7828 0.983117C13.9727 1.17303 14.0676 1.39835 14.0676 1.65908C14.0676 1.91978 13.9727 2.1451 13.7828 2.33501L6.12604 9.99172C5.93613 10.1816 5.71345 10.2766 5.45802 10.2766C5.20259 10.2766 4.97992 10.1816 4.79 9.99172L1.21663 6.41835C1.02671 6.22843 0.934389 6.00312 0.939667 5.74241C0.944959 5.48169 1.04256 5.25637 1.23248 5.06645C1.4224 4.87653 1.64772 4.78158 1.90844 4.78158C2.16915 4.78158 2.39446 4.87653 2.58438 5.06645L5.45802 7.95593Z" fill="#188D52" />
                            </svg>
                            <p className={styles.fileName}>{fileName ?? 'No file selected'}</p>
                        </div>
                    ))
                }
                {
                    fileNames.length === 0 && <p className={styles.fileName}>No file selected</p>
                }
            </div>
        </div>
    )
})

export default CommonFileUploadInput